/**
 * @file   src\components\SelectLocationModal.tsx
 * @brief  SelectLocationModal component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { React, useEffect, useNavigate, useParams, useState } from '../utils/thirdpartyComponents'
import RadioButton from './RadioButton';
import { Button, Offcanvas } from "react-bootstrap";
import "../components/OffCanvas.scss";
import { CommonMessages } from 'src/messages/validationMessages';
import { LocationProperty, locationUpdateRequest } from 'src/interfaces/generalInterface';
import { ILocation, IMappedLocation } from 'src/interfaces/authInterface';
import { LocationApi } from 'src/utils/apiUrls';
import { pageURLs } from 'src/utils/pageUrls';
import axios from '../config/axiosConfig';
import { MessageToaster } from 'src/utils/toastUtil';
import { syncFreeSingleWashByLocation, setActiveLocale } from "src/utils/helper";

const SelectLocationModal = (props: any) => {
    const navigate = useNavigate()
    // Instantiate MessageToaster
    const toaster = new MessageToaster();
    // Route information variable
    const params = useParams();

    const { show, onHide, locationReload, selectedLocation } = props;

    const handleOnHide = () => {
        onHide();
    }

    // Initialise state for list of locations
    const [locations, setLocations] = useState<IMappedLocation[]>([])


    // State to handle selected location before clicking OK
    const [selected, setSelected] = useState<number>(0)

    // Initialize state for errors
    const [error, setError] = useState<string | null>(null)

    // Need call location fetch API
    useEffect(() => {
        fetchLocation()
    }, []);


    // Function to handle radio button selection
    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setError(null)
        setSelected(parseInt(event.target.value))
    };



    // Function to handle OK button click
    const handleOkButtonClick = async () => {
        if (selected) {
            const location = locations.find(loc => loc.locationId == selected);
            const locationUpdateData: locationUpdateRequest = {
                locationId: selected
            };
            const locationUpdate = await axios.post(LocationApi.LOCATION_UPDATE, locationUpdateData);
            if (locationUpdate?.data?.error === 0) {
                if (location) {
                    setActiveLocale({
                        countryCode: location.countryCode,
                        currency: location.currency,
                        locale: location.locale,
                        timezone: location.timezone,
                    });
                }
                syncFreeSingleWashByLocation(Number(selected || 0));
                localStorage.setItem('selectedBay','') 
                localStorage.removeItem('selectedBay')  // Reset selected bay
                localStorage.removeItem('selectedBayType')
                handleOnHide();
                locationReload();
                navigate("/" + params.carwashname + "/" + pageURLs.home)
            } else {
                toaster.toastError(locationUpdate.data.error);
            }


        } else {
            setError(CommonMessages.location_required)
        }
    };

    const fetchLocation = async () => {
        const locationData = await axios.get(LocationApi.LOCATION_FETCH);
        if (locationData?.data?.data?.length === 1) {
            const locationId = locationData?.data?.data[0]?.locationId
            const locationUpdateData: locationUpdateRequest = {
                locationId: locationId
            };
            const locationUpdate = await axios.post(LocationApi.LOCATION_UPDATE, locationUpdateData);
            if (locationUpdate?.data?.error === 0) {
                syncFreeSingleWashByLocation(Number(locationId || 0));
                navigate("/" + params.carwashname + "/" + pageURLs.home)
            } else {
                toaster.toastError(locationUpdate.data.error);
            }
        } else {
            const addressdata: ILocation[] = locationData?.data?.data
            const locations: IMappedLocation[] = addressdata?.map(location => {
                let addressArray = [];
                if(location.hasDefaultAddress) {
                    addressArray.push(location.companyAddress1);
                    addressArray.push(location.companyAddress2)
                    addressArray.push(location.companyCity)
                    addressArray.push(location.companyState)
                    addressArray.push(location.companyZipCode)
                } else {
                    addressArray.push(location.address1);
                    addressArray.push(location.address2)
                    addressArray.push(location.city)
                    addressArray.push(location.state)
                    addressArray.push(location.zipCode)
                }
                
                var filteredAddress = addressArray?.filter(function (el) {
                    return (el);
                });
                const address:string = filteredAddress?.join(', ');
                
                return {
                    locationId: location.locationId,
                    locationName: location.locationName,
                    isActive: location.isActive,
                    hasDefaultAddress: location.hasDefaultAddress,
                    address1 : address
                };
            });
            setLocations(locations);
        }
    }

    return (
        <Offcanvas
            id="location-offcanvas"
            show={show}
            onHide={() => handleOnHide()}
            {...props}
            placement="bottom"
        >
            <Offcanvas.Header closeButton>
                <h4>Select Wash Location</h4>
            </Offcanvas.Header>

            <Offcanvas.Body>

                {
                    // To remove selected location from list
                    locations?.length > 0 && locations.filter((loc) => loc.locationId != selectedLocation).map((location, index) => (
                        <div key={index} className="location-info-card">
                            <RadioButton name={"select_location"} label={location.locationName}  value={location.locationId} description={location.address1 ? location.address1 : ""} onChange={handleRadioChange} />
                        </div>
                    ))
                }
            </Offcanvas.Body>

            <div className="offcanvas-footer">
                {error ? <span className="error-msg">{error}</span> : null}
                <Button variant="primary" className="primary-btn btn-main" onClick={handleOkButtonClick}>OK</Button>
            </div>
        </Offcanvas>
    );
};
export default SelectLocationModal;
